import React, { type ComponentType } from 'react';
import { useResource, type RouteResource } from '@atlassian/react-resource-router';

import { useSummaryData } from '../../../controllers/summary-data/index.tsx';
import { RenderSuccess } from '../../utils.tsx';
import type { BoundaryProps } from '../with-boundary/index.tsx';

export type DataProps<T> = {
	data: T;
};

export const withSummaryData = <T, P extends {}>({
	Component,
	fetchDataFn,
	FallbackComponent,
}: {
	Component: ComponentType<P & DataProps<T>>;
	fetchDataFn: () => Promise<T>;
	FallbackComponent: ComponentType<{ handleRetry: () => void }>;
}) => {
	return ({
		LoadingComponent,
		onSuccess,
		onError,
		...componentProps
	}: Omit<P, keyof DataProps<T>> & BoundaryProps) => {
		const { fetchData: refresh, ...dataHookProps } = useSummaryData(fetchDataFn);

		const props = {
			dataHookProps,
			refresh,
			loadingComponent: <LoadingComponent />,
			fallbackComponent: <FallbackComponent handleRetry={refresh} />,
			onSuccess,
			onError,
			/**
			 * Typescript has difficulty inferring/converting types with more than one layer of `Omit`
			 * so we need to do some typecasting although the resulting types are identical
			 */
			/* eslint-disable @typescript-eslint/consistent-type-assertions */
			Component: Component as ComponentType<Omit<P, keyof DataProps<T>> & DataProps<T>>,
			componentProps: componentProps as unknown as Omit<P, keyof DataProps<T>>,
			/* eslint-enable @typescript-eslint/consistent-type-assertions */
		};

		return <ComponentWithFallbackLoading {...props} />;
	};
};

// TODO clean up or refactor as a part of HL-1930
export const withRouteResourceData = <D, T, P extends {}>({
	Component,
	routeResource,
	dataSelector,
	FallbackComponent,
}: {
	Component: ComponentType<P & DataProps<T>>;
	routeResource: RouteResource<D>;
	dataSelector: (data: D) => T;
	FallbackComponent: ComponentType<{ handleRetry: () => void }>;
}) => {
	return ({
		LoadingComponent,
		onSuccess,
		onError,
		...componentProps
	}: Omit<P, keyof DataProps<T>> & BoundaryProps) => {
		const { data: routeResourceData, loading, error, refresh } = useResource(routeResource);
		const data = routeResourceData && dataSelector(routeResourceData);

		const props = {
			dataHookProps: {
				data,
				loading,
				error,
			},
			refresh,
			loadingComponent: <LoadingComponent />,
			fallbackComponent: <FallbackComponent handleRetry={refresh} />,
			onSuccess,
			onError,
			/**
			 * Typescript has difficulty inferring/converting types with more than one layer of `Omit`
			 * so we need to do some typecasting although the resulting types are identical
			 */
			/* eslint-disable @typescript-eslint/consistent-type-assertions */
			Component: Component as ComponentType<Omit<P, keyof DataProps<T>> & DataProps<T>>,
			componentProps: componentProps as unknown as Omit<P, keyof DataProps<T>>,
			/* eslint-enable @typescript-eslint/consistent-type-assertions */
		};

		return <ComponentWithFallbackLoading {...props} />;
	};
};

const ComponentWithFallbackLoading = <T, P>(props: {
	dataHookProps: {
		data: T | null;
		loading: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		error: Error | Record<string, any> | null;
	};
	loadingComponent: JSX.Element;
	fallbackComponent: JSX.Element;
	onSuccess: () => void;
	onError: (errorMessage: string) => void;
	Component: ComponentType<P & DataProps<T>>;
	componentProps: P;
}) => {
	const {
		dataHookProps: { data, loading, error },
		loadingComponent,
		onError,
		fallbackComponent,
		onSuccess,
		Component,
		componentProps,
	} = props;

	if (!data || (loading && !data)) {
		return loadingComponent;
	}

	if (error) {
		onError(error?.message);
		return fallbackComponent;
	}

	return (
		<RenderSuccess onSuccess={onSuccess}>
			<Component {...componentProps} data={data} />
		</RenderSuccess>
	);
};
